import './LoginModal.scss';
import { Application } from '@mvecom/common/enums/Application';
import appMetadata from '@mvecom/common/utils/appMetadata';
import { isAxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Actions } from '../../../actions/enums';
import { useUnifiedAuth } from '../../../hooks/unifiedAuth/useUnifiedAuth';
import { getEnv } from '../../../utils/env';
import { getAppName } from '../../../utils/getAppName';
import { isCiamEnabled } from '../../../utils/retailUnitsWithCiam';
import { CiamLoginModalContent } from '../../Login/CiamLoginModalContent';
import { LoginPage } from '../../Login/IcapsLoginModalContent';
import { Wrapper } from '../standardModalWrapper';
import { FailScreen } from './FailScreen/FailScreen';
import { CardInfo } from './LoyaltyCard/LoyaltyCard';
import { MaintenanceMode } from './MaintenanceMode/MaintenanceMode';
import { MenuLoggedIn } from './Menu/Menu';

const isEstonianReleaseWindow = () => {
  if (appMetadata.getRetailUnit() !== 'ee' || window.location.host !== 'www.ikea.com') {
    return false;
  }
  const now = new Date();
  const releaseDate = new Date('2026-06-17T07:30:00+02:00');
  return now < releaseDate;
};

export const LoginModal = () => {
  // Enabling the maintenance mode will display a different view
  // explaining to the user that login is currently unavailable
  const isMaintenanceModeEnabled = isEstonianReleaseWindow();

  const { t } = useTranslation('profile');

  const { authState, setAuthState, retryMenu, logout, icapsLogin } = useUnifiedAuth(isCiamEnabled ? 'ciam' : 'icaps');
  const close = useCallback(() => setAuthState('Closed'), [setAuthState]);

  // listen to pubsub messages for opening the menu
  useEffect(() => {
    const open = () => {
      if (globalThis.ikea.mvAgent.profilePagesFragmentLive || getAppName() === Application.PROFILE) {
        return;
      }

      setAuthState('Open');
    };

    const openLoginReminder = () => {
      if (Cookies.get('_session_id')) {
        globalThis.ikea.pubsub.publish(Actions.GO_TO_CHECKOUT);
      } else {
        setAuthState('Reminder');
      }
    };

    globalThis?.ikea?.pubsub?.subscribe(Actions.PROFILE_OPEN_MENU, open);
    globalThis?.ikea?.pubsub?.subscribe(Actions.PROFILE_OPEN_LOGIN_REMINDER, openLoginReminder);

    return () => {
      globalThis?.ikea?.pubsub?.unsubscribe(Actions.PROFILE_OPEN_MENU, open);
      globalThis?.ikea?.pubsub?.unsubscribe(Actions.PROFILE_OPEN_LOGIN_REMINDER, openLoginReminder);
    };
  }, [setAuthState]);

  const loyaltyCard = authState.status === 'Loyalty' ? authState.data : undefined;

  if (authState.status === 'Closed') {
    return <Wrapper visible={false} close={close} />;
  }

  if (authState.status === 'CheckingIfLoggedIn') {
    return <Wrapper visible loading close={close} />;
  }

  // for logged in states
  const searchParams = new URLSearchParams(globalThis.location.search);
  const showTestParam = searchParams.get('secretlyTestMaintenance') === 'true';
  const env = getEnv();

  if (isMaintenanceModeEnabled || (showTestParam && env !== 'production')) {
    return (
      <Wrapper visible close={close}>
        <MaintenanceMode dismiss={logout} />
      </Wrapper>
    );
  }

  if (
    authState.status === 'Unauthenticated' ||
    authState.status === 'Reminder' ||
    authState.status === 'ReminderMergeCheck'
  ) {
    if (isCiamEnabled) {
      // we don't do reminder modal for ciam
      return (
        <Wrapper ariaLabel={t('sideMenuLoggedOut.ariaLabel')} visible close={close}>
          <CiamLoginModalContent />
        </Wrapper>
      );
    }

    if (
      authState.status !== 'ReminderMergeCheck' &&
      isAxiosError(authState.error) &&
      authState.error.response?.status === 503
    ) {
      return (
        <Wrapper visible close={close}>
          <MaintenanceMode dismiss={close} />
        </Wrapper>
      );
    }

    return (
      <Wrapper ariaLabel={t('sideMenuLoggedOut.ariaLabel')} visible close={close}>
        <LoginPage
          login={icapsLogin}
          loading={authState.status === 'ReminderMergeCheck' || authState.isLoggingIn}
          loginError={authState.status === 'ReminderMergeCheck' ? undefined : authState.error}
        />
      </Wrapper>
    );
  }

  if (authState.status === 'MenuLoading') {
    return <Wrapper loading visible close={close} />;
  }

  if (authState.status === 'MenuError') {
    if (isAxiosError(authState.error) && authState.error.response?.status === 503) {
      return (
        <Wrapper visible close={close}>
          <MaintenanceMode dismiss={logout} />
        </Wrapper>
      );
    }
    return (
      <Wrapper visible close={close}>
        <FailScreen logout={logout} retry={retryMenu} />
      </Wrapper>
    );
  }

  if (authState.status === 'Menu') {
    return (
      <Wrapper noPadding visible close={close}>
        <MenuLoggedIn menu={authState.data} logout={logout} onLoyaltyCardClick={() => setAuthState('Loyalty')} />
      </Wrapper>
    );
  }

  if (authState.status === 'LogoutError' && authState.data) {
    return (
      <Wrapper noPadding visible close={close}>
        <MenuLoggedIn
          logoutError
          menu={authState.data}
          logout={logout}
          onLoyaltyCardClick={() => setAuthState('Loyalty')}
        />
      </Wrapper>
    );
  }

  if (authState.status === 'LogoutError') {
    return (
      <Wrapper noPadding visible close={close}>
        <FailScreen logout={logout} retry={retryMenu} />
      </Wrapper>
    );
  }

  if (authState.status === 'Loyalty') {
    return (
      <Wrapper
        title={authState.data.title}
        noPadding
        visible
        close={close}
        back={loyaltyCard ? () => setAuthState('Open') : undefined}
      >
        {authState.status === 'Loyalty' && <CardInfo loyaltyCard={authState.data} />}
      </Wrapper>
    );
  }

  authState satisfies never;
};
